import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { OfferTypeSelector } from '../components/offers/OfferTypeSelector';
import { OfferForm } from '../components/offers/OfferForm';
import { MOCK_OFFERS } from '../lib/mockData';
import { Offer, OfferType } from '../types';
import { Plus, Pencil, Trash2, Copy, MapPin, Percent, TrendingUp, CalendarClock, Tags } from 'lucide-react';
import { formatDate } from '../lib/utils';
export function OfferManagement() {
  const [view, setView] = useState<'list' | 'select-type' | 'create' | 'edit'>('list');
  const [selectedType, setSelectedType] = useState<OfferType>('fixed');
  const [editingOffer, setEditingOffer] = useState<Offer | undefined>(undefined);
  const [offers, setOffers] = useState<Offer[]>(MOCK_OFFERS);
  const handleCreateStart = () => {
    setView('select-type');
  };
  const handleTypeSelect = (type: OfferType) => {
    setSelectedType(type);
    setView('create');
  };
  const handleEdit = (offer: Offer) => {
    setEditingOffer(offer);
    setSelectedType(offer.type);
    setView('edit');
  };
  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this offer?')) {
      setOffers(prev => prev.filter(o => o.id !== id));
    }
  };
  const handleDuplicate = (offer: Offer) => {
    const newOffer = {
      ...offer,
      id: `off_${Date.now()}`,
      name: `${offer.name} (Copy)`,
      status: 'inactive' as const,
      createdAt: new Date().toISOString()
    };
    setOffers(prev => [newOffer, ...prev]);
  };
  const handleSave = (offerData: Partial<Offer>) => {
    if (view === 'create') {
      const newOffer = {
        ...offerData,
        id: `off_${Date.now()}`,
        createdAt: new Date().toISOString()
      } as Offer;
      setOffers(prev => [newOffer, ...prev]);
    } else if (view === 'edit' && editingOffer) {
      setOffers(prev => prev.map(o => o.id === editingOffer.id ? {
        ...o,
        ...offerData
      } as Offer : o));
    }
    setView('list');
    setEditingOffer(undefined);
  };
  const getOfferIcon = (type: OfferType) => {
    switch (type) {
      case 'fixed':
        return <Percent className="h-4 w-4" data-id="element-503" />;
      case 'tiered':
        return <TrendingUp className="h-4 w-4" data-id="element-504" />;
      case 'promotional':
        return <CalendarClock className="h-4 w-4" data-id="element-505" />;
      case 'category':
        return <Tags className="h-4 w-4" data-id="element-506" />;
    }
  };
  const getOfferDetails = (offer: Offer) => {
    switch (offer.type) {
      case 'fixed':
        return <span className="font-medium" data-id="element-507">
            {offer.cashbackPercentage}% Cashback
          </span>;
      case 'tiered':
        const min = Math.min(...offer.tiers.map(t => t.cashbackPercentage));
        const max = Math.max(...offer.tiers.map(t => t.cashbackPercentage));
        return <span className="font-medium" data-id="element-508">
            {min}% - {max}% Tiered
          </span>;
      case 'promotional':
        return <div className="flex flex-col" data-id="element-509">
            <span className="font-medium text-purple-600" data-id="element-510">
              {offer.promotionalPercentage}% Promo
            </span>
            <span className="text-xs text-slate-500 line-through" data-id="element-511">
              {offer.standardPercentage}% Standard
            </span>
          </div>;
      case 'category':
        return <span className="font-medium" data-id="element-512">
            {offer.categories.length} Categories
          </span>;
    }
  };
  if (view === 'select-type') {
    return <div className="space-y-6" data-id="element-513">
        <div className="flex items-center gap-4 mb-6" data-id="element-514">
          <Button variant="ghost" onClick={() => setView('list')} data-id="element-515">
            Cancel
          </Button>
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-516">
            Select Offer Type
          </h2>
        </div>
        <OfferTypeSelector onSelect={handleTypeSelect} data-id="element-517" />
      </div>;
  }
  if (view === 'create' || view === 'edit') {
    return <OfferForm type={selectedType} initialData={editingOffer} onSave={handleSave} onCancel={() => {
      setView('list');
      setEditingOffer(undefined);
    }} data-id="element-518" />;
  }
  return <div className="space-y-8" data-id="element-519">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-520">
        <div data-id="element-521">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-522">
            Offer Management
          </h2>
          <p className="text-slate-500 mt-1" data-id="element-523">
            Create and manage your cashback offers.
          </p>
        </div>
        <Button onClick={handleCreateStart} className="bg-[#218080] hover:bg-[#1a6666]" data-id="element-524">
          <Plus className="h-4 w-4 mr-2" data-id="element-525" />
          Create Offer
        </Button>
      </div>

      <Card data-id="element-526">
        <CardHeader data-id="element-527">
          <CardTitle data-id="element-528">All Offers</CardTitle>
        </CardHeader>
        <CardContent data-id="element-529">
          <div className="overflow-x-auto" data-id="element-530">
            <table className="w-full text-sm text-left" data-id="element-531">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-532">
                <tr data-id="element-533">
                  <th className="px-4 py-3" data-id="element-534">Offer Name</th>
                  <th className="px-4 py-3" data-id="element-535">Type</th>
                  <th className="px-4 py-3" data-id="element-536">Status</th>
                  <th className="px-4 py-3" data-id="element-537">Rate / Details</th>
                  <th className="px-4 py-3" data-id="element-538">Locations</th>
                  <th className="px-4 py-3 text-right" data-id="element-539">Actions</th>
                </tr>
              </thead>
              <tbody data-id="element-540">
                {offers.length === 0 ? <tr data-id="element-541">
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500" data-id="element-542">
                      No offers yet. Create your first offer to start rewarding
                      customers.
                    </td>
                  </tr> : offers.map(offer => <tr key={offer.id} className="border-b border-slate-100 hover:bg-slate-50" data-id="element-543">
                      <td className="px-4 py-3" data-id="element-544">
                        <div data-id="element-545">
                          <p className="font-medium text-slate-900" data-id="element-546">
                            {offer.name}
                          </p>
                          <p className="text-xs text-slate-500 truncate max-w-[200px]" data-id="element-547">
                            {offer.description}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-548">
                        <div className="flex items-center gap-2" data-id="element-549">
                          {getOfferIcon(offer.type)}
                          <span className="capitalize" data-id="element-550">{offer.type}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-551">
                        <Badge variant={offer.status === 'active' ? 'success' : offer.status === 'expired' ? 'destructive' : offer.status === 'scheduled' ? 'info' : 'secondary'} data-id="element-552">
                          {offer.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" data-id="element-553">{getOfferDetails(offer)}</td>
                      <td className="px-4 py-3" data-id="element-554">
                        <div className="flex items-center gap-1 text-slate-500" data-id="element-555">
                          <MapPin className="h-3 w-3" data-id="element-556" />
                          {offer.locations.length} locations
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-557">
                        <div className="flex justify-end gap-2" data-id="element-558">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(offer)} title="Edit" data-id="element-559">
                            <Pencil className="h-4 w-4 text-slate-500" data-id="element-560" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDuplicate(offer)} title="Duplicate" data-id="element-561">
                            <Copy className="h-4 w-4 text-slate-500" data-id="element-562" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(offer.id)} title="Delete" data-id="element-563">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-564" />
                          </Button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>;
}